/**
 * B631 leg (a) — tenant API keys, the management doors (CLASS A, RULED
 * 2026-10-05, Pathik: "B631a yes"). A key is minted by the platform for a
 * storefront the tenant builds itself: only its digest is stored, and the
 * raw `tk_test_…` / `tk_live_…` bearer is handed over ONCE through Secrets
 * Manager under the tenant's prefix — the OTA key's custody (B526 e4) — so
 * neither the mint's response nor the list carries anything a caller could
 * present. The strict responses below have no field a bearer could ride.
 *
 * A key's scopes are FROZEN at mint and are the buyer path only (the
 * vocabulary mirrors @transact/domain's API_KEY_SCOPES — pinned equal by
 * the handlers' test). Every door is ADMIN on the tenant-config scope, the
 * OTA key's own: a key is a sales channel's credential.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

const zApiKeyScope = z.enum(["catalog:read", "availability:read", "cart:write", "checkout:write", "orders:read", "tickets:read", "webhooks:manage"]);

/**
 * B631 (d) — the key scope that admits each §11 permission, as the reference
 * documents it: the kernel's own derivation (the FIRST scope, in vocabulary
 * order, whose grants hold the permission — @transact/domain's
 * API_KEY_GRANTS), mirrored here because contracts carry no domain
 * dependency; handlers' b631-api-key-scope-map test pins the two equal.
 */
export const API_KEY_SCOPE_FOR_PERMISSION: Readonly<Record<string, z.infer<typeof zApiKeyScope>>> = Object.freeze({
  "catalog:read": "catalog:read",
  "supply:read": "availability:read",
  "order:write": "cart:write",
  "payment:write": "checkout:write",
  "order:read": "orders:read",
  "entitlement:read": "tickets:read",
  "webhook:write": "webhooks:manage",
});

/** B631 (a4) — the per-key budget the kernel enforces, as the reference states it (pinned to handlers' API_KEY_RATE_LIMIT). */
export const API_KEY_RATE_LIMIT_PER_MINUTE = 600;

const zApiKeySummary = z
  .object({
    keyId: zId,
    name: z.string().min(1).max(60),
    mode: z.enum(["test", "live"]),
    scopes: z.array(zApiKeyScope).min(1),
    /** The secret's last four — "tk_test_…WxYz" in the list. Never the secret. */
    last4: z.string().min(4).max(4),
    issuedAt: zEpochMs,
    issuedBy: z.string().min(1),
    expiresAt: zEpochMs.optional(),
    revokedAt: zEpochMs.optional(),
    revokedReason: z.string().min(1).optional(),
    status: z.enum(["ACTIVE", "EXPIRED", "REVOKED"]),
  })
  .strict();

const EXAMPLE_KEY = {
  keyId: "k8f3Hq2LmNp4RsT6",
  name: "Our website",
  mode: "test" as const,
  scopes: ["catalog:read", "availability:read", "cart:write", "checkout:write"] as ("catalog:read" | "availability:read" | "cart:write" | "checkout:write")[],
  last4: "WxYz",
  issuedAt: 1_790_000_000_000,
  issuedBy: "staff-1",
  status: "ACTIVE" as const,
};

/**
 * Mint a key. Its scopes must sit inside the minting admin's own authority
 * (refused by name otherwise); the bearer goes to Secrets Manager
 * (`handover` names the secret) and NOWHERE else.
 */
export const apiKeyMint = writeOp({
  name: "apikey.mint",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  gate: "admin",
  fields: { name: z.string().min(1).max(200), scopes: z.array(zApiKeyScope).min(1).max(6), expiresAt: zEpochMs.optional() },
  response: z.object({ key: zApiKeySummary, handover: z.string().min(1) }).strict(),
  exampleRequest: { name: "Our website", scopes: ["catalog:read", "availability:read", "cart:write", "checkout:write"] },
  exampleResponse: { key: EXAMPLE_KEY, handover: "transact/dev/tenants/t-1/api-keys/k8f3Hq2LmNp4RsT6" },
});

export const apiKeyRevoke = writeOp({
  name: "apikey.revoke",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { keyId: zId, reason: z.string().min(1).max(200).optional() },
  response: z.object({ key: zApiKeySummary }).strict(),
  exampleRequest: { keyId: "k8f3Hq2LmNp4RsT6", reason: "rotated" },
  exampleResponse: { key: { ...EXAMPLE_KEY, revokedAt: 1_790_000_100_000, revokedReason: "rotated", status: "REVOKED" as const } },
});

export const apiKeyList = readOp({
  name: "apikey.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z.object({ keys: z.array(zApiKeySummary) }).strict(),
  exampleRequest: {},
  exampleResponse: { keys: [EXAMPLE_KEY] },
});
